import {
  fontFamily,
  monoFontFamily,
  staticVariables,
  themeVariables,
  themes,
} from '@cottonray/design-tokens'

/**
 * 런타임 CSS-in-JS를 쓰지 않는다. 토큰을 CSS 변수로 한 번 깔고,
 * 컴포넌트는 클래스만 붙인다 — 번들에 스타일 엔진이 들어가지 않는다.
 *
 * 라이트가 기본이고(토스 문법, 2026-07-31), 다크는 시스템 설정 또는 `data-theme="dark"`로 전환된다.
 */
function variables(): string {
  return `
:root {
${themeVariables(themes.light)}
${staticVariables()}
  --st-font: ${fontFamily};
  --st-font-mono: ${monoFontFamily};
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
${themeVariables(themes.dark)}
  }
}

:root[data-theme="dark"] {
${themeVariables(themes.dark)}
}

:root[data-theme="light"] {
${themeVariables(themes.light)}
}`
}

function base(): string {
  return `
*, *::before, *::after { box-sizing: border-box; }

html, body {
  margin: 0;
  background: var(--st-color-background);
  color: var(--st-color-text);
  font-family: var(--st-font);
  font-size: var(--st-font-size-md);
  line-height: var(--st-line-height-md);
  -webkit-font-smoothing: antialiased;
}

a { color: var(--st-color-accent); text-decoration: none; }
a:hover { text-decoration: underline; }

code, pre { font-family: var(--st-font-mono); }

:focus-visible {
  outline: 2px solid var(--st-color-accent);
  outline-offset: 2px;
}`
}

function components(): string {
  return `
/* 텍스트 */
.st-text { margin: 0; }
.st-text--display { font-size: var(--st-font-size-4xl); line-height: var(--st-line-height-4xl); font-weight: var(--st-font-weight-bold); letter-spacing: -0.02em; }
.st-text--title { font-size: var(--st-font-size-3xl); line-height: var(--st-line-height-3xl); font-weight: var(--st-font-weight-bold); letter-spacing: -0.01em; }
.st-text--heading { font-size: var(--st-font-size-xl); line-height: var(--st-line-height-xl); font-weight: var(--st-font-weight-bold); letter-spacing: -0.01em; }
.st-text--body { font-size: var(--st-font-size-md); line-height: var(--st-line-height-md); }
.st-text--label { font-size: var(--st-font-size-sm); line-height: var(--st-line-height-sm); font-weight: var(--st-font-weight-semibold); }
.st-text--caption { font-size: var(--st-font-size-xs); line-height: var(--st-line-height-xs); }

.st-tone--default { color: var(--st-color-text); }
.st-tone--muted { color: var(--st-color-text-muted); }
.st-tone--subtle { color: var(--st-color-text-subtle); }
.st-tone--accent { color: var(--st-color-accent); }
.st-tone--danger { color: var(--st-color-danger); }
.st-tone--success { color: var(--st-color-success); }

/* 버튼 */
.st-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--st-space-2);
  border: 0;
  border-radius: var(--st-radius-md);
  font-family: inherit;
  font-size: var(--st-font-size-sm);
  font-weight: var(--st-font-weight-semibold);
  cursor: pointer;
  transition: background var(--st-duration-fast) ease, opacity var(--st-duration-fast) ease;
}
.st-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.st-btn--sm { min-height: 36px; padding: var(--st-space-2) var(--st-space-3); }
.st-btn--md { min-height: 44px; padding: var(--st-space-3) var(--st-space-4); }
.st-btn--lg { min-height: 52px; padding: var(--st-space-4) var(--st-space-5); font-size: var(--st-font-size-md); }

.st-btn--primary { background: var(--st-color-accent); color: var(--st-color-text-on-accent); }
.st-btn--primary:hover:not(:disabled) { background: var(--st-color-accent-hover); }

/* 토스의 회색 버튼 — 무테, 표면색만 */
.st-btn--secondary { background: var(--st-color-surface-raised); color: var(--st-color-text); }
.st-btn--secondary:hover:not(:disabled) { background: var(--st-color-surface-hover); }

.st-btn--ghost { background: transparent; color: var(--st-color-accent); }
.st-btn--ghost:hover:not(:disabled) { background: var(--st-color-accent-surface); }

.st-btn--full { width: 100%; }

/* 셀렉트 — 네이티브 화살표를 지우고 배경 이미지로 다시 그린다(색을 토큰에 맞추기 위해) */
.st-select {
  appearance: none;
  background-color: var(--st-color-surface-raised);
  background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),
    linear-gradient(135deg, currentColor 50%, transparent 50%);
  background-position: right var(--st-space-4) center, calc(100% - var(--st-space-4) + 5px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  border: 0;
  border-radius: var(--st-radius-md);
  color: var(--st-color-text);
  font-family: inherit;
  font-weight: var(--st-font-weight-medium);
  cursor: pointer;
  transition: background-color var(--st-duration-fast) ease;
}
.st-select:hover { background-color: var(--st-color-surface-hover); }

.st-select--sm { min-height: 36px; font-size: var(--st-font-size-sm); padding: 0 var(--st-space-8) 0 var(--st-space-3); }
.st-select--md { min-height: 44px; font-size: var(--st-font-size-md); padding: 0 var(--st-space-10) 0 var(--st-space-4); }

/* 카드 */
.st-card {
  background: var(--st-color-surface);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  padding: var(--st-space-6);
}
.st-card--raised { background: var(--st-color-surface-raised); }

/* 레이아웃 */
.st-stack { display: flex; }
.st-stack--column { flex-direction: column; }
.st-stack--row { flex-direction: row; align-items: center; }

.st-container {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 0 var(--st-space-5);
}

/* 배지 */
.st-badge {
  display: inline-flex;
  align-items: center;
  padding: var(--st-space-1) var(--st-space-2);
  border-radius: var(--st-radius-full);
  font-size: var(--st-font-size-xs);
  font-weight: var(--st-font-weight-medium);
}
.st-badge--neutral { background: var(--st-color-surface-raised); color: var(--st-color-text-muted); }
.st-badge--accent { background: var(--st-color-accent-surface); color: var(--st-color-accent); }
.st-badge--success { background: var(--st-color-success-surface); color: var(--st-color-success); }
.st-badge--danger { background: var(--st-color-danger-surface); color: var(--st-color-danger); }

/* 브랜드 마크 — 액센트 토큰을 따라가고, 크기는 옆에 놓인 글자를 따라간다.
   1.15em 은 대문자 높이가 아니라 소문자 x-높이 기준으로 맞춘 값이다 — 'cottonray' 에는
   올라가는 글자가 l 하나뿐이라 캡 높이에 맞추면 마크만 혼자 커 보인다 */
.st-brand-mark {
  width: auto;
  height: 1.15em;
  color: var(--st-color-accent);
  flex-shrink: 0;
}

/*
 * 스켈레톤 — ui-native 와 같은 이름·같은 두 토큰. 색은 skeleton·skeleton-strong 뿐이고
 * 테두리를 그리지 않는다. 위계는 블록의 크기·너비·개수로만 낸다 (DESIGN.md §Colors).
 */
@keyframes st-skeleton-pulse {
  from { background-color: var(--st-color-skeleton); }
  to { background-color: var(--st-color-skeleton-strong); }
}
.st-skeleton {
  background-color: var(--st-color-skeleton);
  border-radius: var(--st-radius-md);
  /* 네이티브(Animated 900ms 왕복)와 같은 호흡 */
  animation: st-skeleton-pulse 900ms ease-in-out infinite alternate;
}
.st-skeleton--line { height: 14px; border-radius: var(--st-radius-sm); }
.st-skeleton--card { padding: var(--st-space-4); }

/*
 * 모션을 줄이겠다고 설정한 사용자에게는 펄스를 멈춘다 — 로딩 자리는 화면에 여러 개가
 * 동시에 깜빡이는 요소라 어지럼을 유발하기 쉽다. 색은 그대로라 자리 표시 기능은 유지된다.
 */
@media (prefers-reduced-motion: reduce) {
  .st-skeleton { animation: none; }
}`
}

export function stylesheet(): string {
  return [variables(), base(), components()].join('\n')
}
